import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '@/lib/api'
import { Plus, FolderTree, Edit, Trash2, X, RefreshCw, Lock, AlertTriangle } from 'lucide-react'

interface Category {
  id: string
  name: string
  type: 'live' | 'movie' | 'series'
  sort_order: number
  is_active: boolean
  channel_range_start?: number | null
  channel_range_end?: number | null
  stable_numbering: boolean
  auto_assign_numbers: boolean
  used_channel_count?: number
  next_channel_number?: number | null
}

interface CategoryFormData {
  name: string
  type: 'live' | 'movie' | 'series'
  sort_order: number
  is_active: boolean
  channel_range_start?: number | null
  channel_range_end?: number | null
  stable_numbering: boolean
  auto_assign_numbers: boolean
}

const defaultFormData: CategoryFormData = {
  name: '',
  type: 'live',
  sort_order: 0,
  is_active: true,
  channel_range_start: null,
  channel_range_end: null,
  stable_numbering: false,
  auto_assign_numbers: true,
}

export default function Categories() {
  const [typeFilter, setTypeFilter] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [formData, setFormData] = useState<CategoryFormData>(defaultFormData)
  const [error, setError] = useState('')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['categories', typeFilter],
    queryFn: () => api.getCategories({ type: typeFilter }),
  })

  const { data: streamStats } = useQuery({
    queryKey: ['streamStats'],
    queryFn: () => api.getStreamStats(),
  })

  const createMutation = useMutation({
    mutationFn: (data: CategoryFormData) => api.createCategory(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['categories'] })
      closeModal()
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to create category')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CategoryFormData }) =>
      api.updateCategory(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['categories'] })
      closeModal()
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to update category')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteCategory(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
  })

  const renumberMutation = useMutation({
    mutationFn: (id: string) => api.renumberCategory(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
    onError: (err: any) => {
      alert(err.response?.data?.error || 'Failed to renumber category')
    },
  })

  const openCreateModal = () => {
    setEditingCategory(null)
    setFormData(defaultFormData)
    setError('')
    setModalOpen(true)
  }

  const openEditModal = (category: Category) => {
    setEditingCategory(category)
    setFormData({
      name: category.name,
      type: category.type,
      sort_order: category.sort_order,
      is_active: category.is_active,
      channel_range_start: category.channel_range_start ?? null,
      channel_range_end: category.channel_range_end ?? null,
      stable_numbering: category.stable_numbering ?? false,
      auto_assign_numbers: category.auto_assign_numbers ?? true,
    })
    setError('')
    setModalOpen(true)
  }

  const handleRenumber = (category: Category) => {
    if (confirm(`Renumber all streams in "${category.name}"? This will assign sequential channel numbers starting from ${category.channel_range_start}.`)) {
      renumberMutation.mutate(category.id)
    }
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingCategory(null)
    setFormData(defaultFormData)
    setError('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (editingCategory) {
      updateMutation.mutate({ id: editingCategory.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleDelete = (category: Category) => {
    if (confirm(`Delete category "${category.name}"?`)) {
      deleteMutation.mutate(category.id)
    }
  }

  const categories = data || []

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">Categories</h1>
        <button onClick={openCreateModal} className="btn-primary shrink-0">
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Add Category</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 bg-secondary rounded-lg p-1 w-fit overflow-x-auto">
        {['', 'live', 'movie', 'series'].map((type) => (
          <button
            key={type || 'all'}
            onClick={() => setTypeFilter(type)}
            className={`px-4 py-2 rounded-md text-base font-medium transition-colors ${
              typeFilter === type
                ? 'bg-card text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {type ? type.charAt(0).toUpperCase() + type.slice(1) : 'All'}
          </button>
        ))}
      </div>

      {/* Categories Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {/* Unassigned Streams Card */}
        {streamStats && streamStats.unassigned !== undefined && streamStats.unassigned > 0 && (
          <Link
            to="/streams?category=none"
            className="bg-card rounded-lg border border-destructive/50 p-6 hover:border-destructive transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-destructive/10 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <div>
                  <h3 className="text-base font-medium text-foreground">Unassigned</h3>
                  <p className="text-sm text-muted-foreground">Needs category</p>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Streams without category</span>
                <span className="text-lg font-semibold text-destructive">{streamStats.unassigned}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                These streams won't have channel numbers in the M3U output
              </p>
            </div>
          </Link>
        )}

        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-card rounded-lg border border-border p-6 animate-pulse">
              <div className="h-12 w-12 bg-muted rounded-lg mb-4"></div>
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
            </div>
          ))
        ) : categories.length > 0 ? (
          categories.map((category: Category) => (
            <div
              key={category.id}
              className="bg-card rounded-lg border border-border p-6 hover:border-muted-foreground/30 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <FolderTree className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-medium text-foreground">{category.name}</h3>
                      {category.stable_numbering && (
                        <span title="Stable numbering">
                          <Lock className="w-3 h-3 text-muted-foreground" />
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground capitalize">{category.type}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  {category.channel_range_start != null && category.channel_range_end != null && !category.stable_numbering && (
                    <button
                      onClick={() => handleRenumber(category)}
                      disabled={renumberMutation.isPending}
                      className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      title="Renumber streams"
                    >
                      <RefreshCw className={`w-4 h-4 ${renumberMutation.isPending ? 'animate-spin' : ''}`} />
                    </button>
                  )}
                  <button
                    onClick={() => openEditModal(category)}
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(category)}
                    className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Channel Range Info */}
              {category.channel_range_start != null && category.channel_range_end != null && (
                <div className="mt-3 p-3 bg-secondary/50 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Channel Range</span>
                    <span className="font-mono text-foreground">
                      {category.channel_range_start} - {category.channel_range_end}
                    </span>
                  </div>
                  {category.used_channel_count !== undefined && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                        <span>{category.used_channel_count} used</span>
                        <span>{category.channel_range_end - category.channel_range_start + 1 - category.used_channel_count} available</span>
                      </div>
                      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{
                            width: `${(category.used_channel_count / (category.channel_range_end - category.channel_range_start + 1)) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Sort: {category.sort_order}</span>
                <span className={category.is_active ? 'badge-success' : 'badge-destructive'}>
                  {category.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-base text-muted-foreground">
            No categories found
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-card rounded-lg border border-border p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-base">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="form-label">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                  className="input-field"
                >
                  <option value="live">Live</option>
                  <option value="movie">Movie</option>
                  <option value="series">Series</option>
                </select>
              </div>

              <div>
                <label className="form-label">Sort Order</label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  className="input-field"
                />
              </div>

              {/* Channel Number Range Section */}
              <div className="border-t border-border pt-4 mt-4">
                <h3 className="text-sm font-medium text-foreground mb-3">Channel Number Range</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Set a range to group channels in Emby's guide. Leave empty to disable.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="form-label">Range Start</label>
                    <input
                      type="number"
                      value={formData.channel_range_start ?? ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        channel_range_start: e.target.value ? parseInt(e.target.value) : null
                      })}
                      className="input-field"
                      placeholder="e.g., 1000"
                    />
                  </div>
                  <div>
                    <label className="form-label">Range End</label>
                    <input
                      type="number"
                      value={formData.channel_range_end ?? ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        channel_range_end: e.target.value ? parseInt(e.target.value) : null
                      })}
                      className="input-field"
                      placeholder="e.g., 1099"
                    />
                  </div>
                </div>

                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="cat_stable_numbering"
                      checked={formData.stable_numbering}
                      onChange={(e) => setFormData({ ...formData, stable_numbering: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <label htmlFor="cat_stable_numbering" className="text-sm text-foreground">
                        Stable Numbering
                      </label>
                      <p className="text-xs text-muted-foreground">
                        Prevents auto-renumbering (recommended for sports)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="cat_auto_assign"
                      checked={formData.auto_assign_numbers}
                      onChange={(e) => setFormData({ ...formData, auto_assign_numbers: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <label htmlFor="cat_auto_assign" className="text-sm text-foreground">
                        Auto-assign Numbers
                      </label>
                      <p className="text-xs text-muted-foreground">
                        Automatically assign channel numbers to new streams
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="cat_is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="cat_is_active" className="text-base text-foreground">Active</label>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeModal} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingCategory
                    ? 'Update'
                    : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
